/**
 * Vincent B982 (Eric 2026-02-26 SPEC STRICK PROD) — Page dédiée pour la
 * régénération massive des procédures. Extrait du panel « Backfill
 * procédures » de la page Diagnostics pour donner à cette action un
 * entry-point discoverable dans la sidebar (Concepteurs > Génération
 * de connaissances > Procédures : regénération massive).
 */
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../../lib/uiDialogs";
import { Play } from "@phosphor-icons/react";
import { useLang } from "../../contexts/LanguageContext";
import { API_BASE as API } from "../../lib/apiBase";

export default function ProceduresRegenerationPage() {
  const { lang } = useLang();

  return (
    <div className="max-w-3xl mx-auto p-6" data-testid="procedures-regeneration-page">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-zinc-900 mb-1">
          {lang === "fr" ? "Procédures : regénération massive" : "Procedures: bulk regeneration"}
        </h1>
        <p className="text-sm text-zinc-600">
          {lang === "fr"
            ? "Relance la génération des procédures pour toutes les vidéos transcrites de l'organisation qui n'en ont pas encore."
            : "Regenerate procedures for every transcribed video in this organization that doesn't have them yet."}
        </p>
      </div>

      <section
        className="bg-purple-50/50 border border-purple-200 rounded-xl p-5 mb-6"
        data-testid="backfill-procedures-panel"
      >
        <div className="flex items-center gap-2 mb-2">
          <Play size={18} weight="bold" className="text-purple-700" />
          <h2 className="text-lg font-bold text-purple-900">
            {lang === "fr" ? "Backfill procédures (org courante)" : "Procedures backfill (current org)"}
          </h2>
        </div>
        <p className="text-xs text-zinc-700 mb-3">
          {lang === "fr"
            ? "Génère les procédures pour toutes les vidéos transcrites de cette org qui n'en ont pas encore (utile après upload massif dans une nouvelle org type FLHQ). Chaque vidéo prend 2-3 min."
            : "Generate procedures for every transcribed video in this org that doesn't have them yet (useful after bulk-upload into a new org like FLHQ). Each video takes 2-3 min."}
        </p>
        <button
          onClick={async () => {
            const ok = await confirmDialog(lang === "fr"
              ? "Lancer la génération des procédures pour TOUTES les vidéos de cette org sans procédures ?\nCela peut prendre 5-30 minutes selon le nombre de vidéos."
              : "Generate procedures for ALL videos in this org without procedures?\nThis may take 5-30 minutes.");
            if (!ok) return;
            try {
              const { data } = await axios.post(`${API}/api/admin/backfill-procedures`, {}, { withCredentials: true, timeout: 30000 });
              if ((data?.videos_queued || 0) === 0) {
                toast.info(lang === "fr" ? "Toutes les vidéos ont déjà des procédures." : "All videos already have procedures.");
              } else {
                toast.success(lang === "fr"
                  ? `${data.videos_queued} vidéo(s) en cours. Rechargez dans 5-15 min.`
                  : `${data.videos_queued} video(s) queued. Reload in 5-15 min.`);
              }
            } catch (e) {
              toast.error(e.response?.data?.detail || (lang === "fr" ? "Erreur" : "Error"));
            }
          }}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-purple-700 hover:bg-purple-800 text-white rounded font-medium text-sm"
          data-testid="backfill-procedures-btn"
        >
          <Play size={14} weight="fill" />
          {lang === "fr" ? "Lancer le backfill" : "Run backfill"}
        </button>
      </section>
    </div>
  );
}
